import useStore from "../../../../lib/itn-ui-kit/data/store";
import BaseRoute from "../../../../lib/itn-ui-kit/route/base-route";
import { service } from "../../../../lib/itn-ui-kit/services/service-container";
import {
  buildContactTypeBadges,
  buildContactTypeColors,
  countBadgeConfiguredTypes,
} from "../../../components/cm/Dashboard/FullPages/ClientDetail/utils/contactTypeBadges";
import { userDBService } from "../../../components/ui library/helpers/dbInstances";

import Template from "./template";
import { transformStaticBarData } from "./utils/transformStaticBarData";

const CONTACTS_PAGE_SIZE = 100;
const VIEW_CONTACT_PERMISSION = "core.view_contact";

export default class ClientDetailDetailRoute extends BaseRoute {
  @service("repositories/case-repository") caseRepository;
  @service("repositories/static-bar-repository") staticBarRepository;
  @service("repositories/field-option-repository") fieldOptionRepository;
  @service("repositories/client-detail-contact-repository") clientDetailContactRepository;
  @service("repositories/client-detail-contact-type-repository")
  clientDetailContactTypeRepository;
  @service("repositories/user-to-case-assignment-repository")
  userToCaseAssignmentRepository;
  @service("repositories/case-history-repository") caseHistoryRepository;

  id = "client-detail-detail";
  path = ":id";
  element = (<Template />);
  revalidateOnParams = ["id"];
  revalidateOnQueryKeys = ["rvClientDetail"];
  revalidateOnQueryAnyChange = false;
  ignoreQueryKeys = ["client"];
  allowManualRevalidate = false;

  /**
   * Build the header's contact-type badges for a case, plus the type -> dot color map the
   * Contacts tab reuses so its rows show the same dots.
   *
   * Contacts are paged rather than capped: a single capped request would silently drop
   * types held only by later contacts. Paging stops as soon as every configured type has
   * been seen, so a case only costs more than one request when it has both a lot of
   * contacts and a configured type that none of the early ones use.
   *
   * Permissions are read from IndexedDB rather than current-user-service: that service is
   * populated by App.js, which races this loader on a cold load, whereas IndexedDB is
   * written at login and is what App.js itself reads. Skipping the request outright keeps
   * a 403 (and its Sentry report) from being provoked for users who can't view contacts.
   *
   * Badges are cosmetic, so any remaining failure degrades to "no badges" rather than
   * taking down the rest of the page.
   */
  async loadContactTypeBadges(caseId) {
    const permissions = (await userDBService.get("permissions").catch(() => [])) || [];
    if (!permissions.includes(VIEW_CONTACT_PERMISSION)) {
      return { badges: [], colors: {} };
    }

    const fetchPage = (offset) =>
      this.clientDetailContactRepository
        .query(caseId, { limit: CONTACTS_PAGE_SIZE, offset })
        .catch(() => [])
        .then((page) => (Array.isArray(page) ? page : []));

    const [firstPage, contactTypeOptions] = await Promise.all([
      fetchPage(0),
      this.clientDetailContactTypeRepository.query().catch(() => []),
    ]);

    const configuredTypeCount = countBadgeConfiguredTypes(contactTypeOptions);
    const totalContacts = useStore.getState().getTotalCount("client-detail-contact");

    let contacts = firstPage;
    let badges = buildContactTypeBadges(contacts, contactTypeOptions);

    for (
      let offset = CONTACTS_PAGE_SIZE;
      offset < totalContacts && badges.length < configuredTypeCount;
      offset += CONTACTS_PAGE_SIZE
    ) {
      const page = await fetchPage(offset);
      if (!page.length) {
        break;
      }
      contacts = contacts.concat(page);
      badges = buildContactTypeBadges(contacts, contactTypeOptions);
    }

    return { badges, colors: buildContactTypeColors(contactTypeOptions) };
  }

  async loader({ params }) {
    const { id } = params;

    let _case = null;
    let _caseHistory = null;
    let userToCaseAssignment = null;
    let _clientId = null;
    let staticBarMetaTooltipData = {};
    let staticBarData = {};
    let contactTypeBadges = [];
    let contactTypeColors = {};

    if (!id || id === "undefined") {
      console.error("Case id not found...case");
    } else {
      try {
        // First get the case to extract the clientId
        const caseRes = await this.caseRepository.findRecord(id, {
          noCache: true,
        });
        _case = caseRes;
        _clientId = _case["client"]["id"];

        // Now run the rest of the requests in parallel using the clientId
        const [
          caseHistoryResp,
          userToCaseAssignmentResp,
          [staticBarResp, tooltipData],
          contactTypeResp,
        ] = await Promise.all([
          this.caseHistoryRepository.query({
            caseId: id,
          }),
          this.userToCaseAssignmentRepository.query({
            case: id,
          }),
          Promise.all([
            this.staticBarRepository.query(id),
            this.fieldOptionRepository.query({
              content_type: "staticbar",
              limit: 100,
            }),
          ]),
          this.loadContactTypeBadges(id),
        ]);
        _caseHistory = caseHistoryResp;
        userToCaseAssignment = userToCaseAssignmentResp;

        // Transform static bar data for easier consumption in the frontend
        if (tooltipData && staticBarResp) {
          const { metaTooltip, metaData } = transformStaticBarData(tooltipData, staticBarResp);
          staticBarMetaTooltipData = metaTooltip;
          staticBarData = metaData;
        }

        contactTypeBadges = contactTypeResp.badges;
        contactTypeColors = contactTypeResp.colors;
      } catch (err) {
        console.error("Error loading Client Detail route", err);
      }
    }

    return {
      _case,
      _caseHistory,
      _clientId,
      _caseHistory,
      userToCaseAssignment,
      staticBarMetaTooltipData,
      staticBarData,
      contactTypeBadges,
      contactTypeColors,
    };
  }
}
